import React, { useImperativeHandle, useState, useRef, useEffect, useContext } from 'react';
import {
  Modal,
  Button,
  Form,
  Select,
  Popconfirm,
  InputNumber,
  Divider,
  Spin,
  message,
  Checkbox,
} from 'antd';
import moment from 'moment';
import { EditOutlined } from '@ant-design/icons';

import IntlContext from '@/components/Intl/IntlContext';
import NumberWithHelper from '@/components/Reservation/NumberInput/InputWithHelper';
import { parseFen2Currency } from '@/utils/number';
import { renderIntlLabel, storage } from '@/utils';
import {
  queryInvitePrepare,
  queryInviteHotelCoupon,
  queryInviteCalculation,
  queryInviteSubmitOrder,
  queryCrsPointsSwitch,
} from '@/api/crsInvitation';
import { DATE_FORMAT } from '@/config';
import { getTimeByUtcOffset } from '@/utils/intl';
import InviteModal from '../../InviteModal';
import CouponSelector, { CouponSelectorRef } from '../../CouponSelector/index';

import styles from './index.module.scss';

const { Option } = Select;

interface IProps {
  onConfirm: () => void;
}

export interface ConfirmModalRef {
  showModal: (data: any) => void;
}

const FormItem = Form.Item;

export default React.forwardRef<ConfirmModalRef, IProps>((props, ref) => {
  const { onConfirm } = props;
  const commonCode = storage.get('commonCode');
  const [modalVisible, setModalVisible] = useState<boolean>(false);
  const [dataInfo, setDataInfo] = useState<any>({});
  const [confirmVisible, setConfirmVisible] = useState<boolean>(false);
  // const [remarkText, setRemarkText] = useState<string>('');
  const [inviteOpen, setInviteOpen] = useState(false);
  const [isFirst, setIsFirst] = useState(true);
  const [rentTimeNum, setRentTimeNum] = useState(1);
  const [editting, setEditting] = useState<boolean>(false);
  const [confirmTitle, setConfirmTitle] = useState<string>('');
  const [confirmType, setConfirmType] = useState<string>('');
  const [timeList, setTimelist] = useState<any>([]);
  const [hotelCouponList, setHotelCouponList] = useState<any>([]);
  const [calculation, setCalculation] = useState<any>({});
  const [fixedPrice, setFixedPrice] = useState<any>(null);
  const [allPrice, setAllPrice] = useState<any>(null);
  const [allPriceDiscountAmount, setAllPriceDiscountAmount] = useState<any>(null);
  const [fixedPriceDiscountAmount, setFixedPriceDiscountAmount] = useState<any>(null);
  const [calculationLoading, setCalculationLoading] = useState<boolean>(false);
  const [optionType, setOptionType] = useState<string>('');
  const [selectHotelCouponList, setSelectHotelCouponList] = useState<any>([]);
  const [btnLoading, setBtnLoading] = useState<boolean>(false);
  const [selectValue, setSelectValue] = useState<string>('');
  const [selectOrderNo, setSelectOrderNo] = useState('');
  const intlCtx = useContext(IntlContext);

  const [pointsSwitch, setPointsSwitch] = useState(false);
  // 勾选的积分
  const [selectedPoints, setSelectedPoints] = useState([]);

  const couponSelectorRef = useRef<CouponSelectorRef>({
    openModal: () => {},
    closeModal: () => {},
  });

  const reloadGetInviteCalculation = async (params) => {
    setCalculationLoading(true);
    try {
      const { data, code } = await queryInviteCalculation(params);
      if (code === 0) {
        setSelectHotelCouponList([]);
        setCalculation(data);
        setCalculationLoading(false);
        // setAllPriceDiscountAmount(data.payAmount);
      }
    } catch (error) {
      setCalculationLoading(false);
    }
  };

  const getDateTime = (time) => {
    if (moment(time).format('HH') < '06') {
      return `${getTimeByUtcOffset(moment(time).subtract(1, 'day'), DATE_FORMAT)}`;
    }
    return `${getTimeByUtcOffset(time, DATE_FORMAT)}`;
  };

  const getInviteCalculation = async (params) => {
    setCalculationLoading(true);
    try {
      const { data, code } = await queryInviteCalculation(params);
      if (code === 0) {
        setCalculation(data);
        setCalculationLoading(false);
      }
    } catch (error) {
      setIsFirst(false);
      setCalculationLoading(false);
      setHotelCouponList((list) =>
        list.map((item) => {
          return {
            ...item,
            isChecked: false,
          };
        }),
      );
      reloadGetInviteCalculation({
        ...params,
        couponNos: [],
      });
      // setSelectHotelCouponList([]);
    }
  };

  const resetPrice = () => {
    setFixedPriceDiscountAmount(null);
    setFixedPrice(null);
    setAllPriceDiscountAmount(null);
    setAllPrice(null);

    setOptionType('');
  };

  const getInvitePrepare = async (params) => {
    const { code, data } = await queryInvitePrepare({
      commonCode,
      dayRoomInTime: params.dayRoomInTime,
      dayRoomOutTime: params.dayRoomOutTime,
      hours: params.hours,
      productId: params.productId,
      roomType: params.roomType,
      roomTypeId: params.roomTypeId,
      checkInDate: getDateTime(params.checkInTime),
    });
    if (code === 0) {
      setTimelist(data.timeSelectionResponses);
      const defaultTime = data.timeSelectionResponses.find((val) => val.status === 1);
      setSelectValue(`${defaultTime.startTime}-${defaultTime.endTime}`);
    }
  };

  const getInviteHotelCoupon = async (params) => {
    const { code, data } = await queryInviteHotelCoupon({
      commonCode,
      checkInDate: getDateTime(params.checkInTime),
      departureDate: getDateTime(params.checkOutTime),
      productId: params.productId,
      roomNum: rentTimeNum,
      roomTypeId: params.roomTypeId,
      guestPhone: params.guestPhone,
    });
    if (code === 0) {
      const newCouponList = data.userCoupons.map((item, index) =>
        index < rentTimeNum * params.rentTimeNum
          ? { ...item, isChecked: true }
          : { ...item, isChecked: false },
      );
      const newCouponNos = [];

      newCouponList.forEach((item) => {
        if (item.isChecked) {
          newCouponNos.push(item.couponNo);
        }
      });
      setSelectHotelCouponList(newCouponNos);
      setHotelCouponList(newCouponList);
    }
  };

  const initPointsSwitch = async () => {
    try {
      const res = await queryCrsPointsSwitch();
      if (res.code === 0) {
        // 1 开启
        setPointsSwitch(res.data === 1);
      } else {
        setPointsSwitch(false);
      }
    } catch (error) {
      setPointsSwitch(false);
    }
  };

  // 初始化弹框
  const showModal = async (data) => {
    setPointsSwitch(false);
    setSelectedPoints([]);
    initPointsSwitch();

    if (data.type === 'add') {
      setSelectHotelCouponList([]);
      setRentTimeNum(1);
      // setRemarkText('');
      if (data.occType === 'DAILY') {
        getInviteHotelCoupon(data);
      }
      if (data.occType === 'HOUR') {
        getInvitePrepare(data);
      }
    } else {
      setRentTimeNum(data.roomNum);
      setCalculation(data);
      setSelectValue(`${data.expectedArrivalDate}-${data.expectedDepartureDate}`);
    }
    setDataInfo(data);
    setModalVisible(true);
  };

  useImperativeHandle(ref, () => ({ showModal }), []);

  // 关闭弹框
  const closeModal = () => {
    if (dataInfo.type === 'add') {
      setConfirmTitle(renderIntlLabel('reservation_cmp_modal_leave_confirm_title'));
      setConfirmType('取消');
      setConfirmVisible(true);
    } else {
      setModalVisible(false);
    }
  };

  const closeDetailModal = () => {
    setModalVisible(false);
  };

  // 关闭确认弹窗
  const closeConfirmModal = () => {
    setConfirmVisible(false);
  };

  const closeAllConfirmModal = () => {
    setConfirmVisible(false);
    setModalVisible(false);
    resetPrice();
  };
  const saveConfirmModal = () => {
    setConfirmVisible(false);
  };

  // 确认提交
  const handleSubmitClick = () => {
    setConfirmTitle(renderIntlLabel('channel_crs_msg_confirm_to_create_invitation_order'));
    setConfirmType('确认');
    setConfirmVisible(true);
  };

  const handleDayDurationChange: (value: string) => void = (value) => {
    let rentTime;
    if (value === null) {
      rentTime = 1;
    } else {
      if (!/^\d*$/.test(value)) return;
      if (value) {
        rentTime = Number(value);
        if (rentTime < 1) {
          message.error(renderIntlLabel('channel_crs_msg_at_lease_select_one_room'));
          return;
        }
        if (rentTime > dataInfo.roomQuantity) {
          message.error(renderIntlLabel('channel_crs_msg_reach_limit_of_reserve_rooms'));
          return;
        }
        if (rentTime < 1 || rentTime > dataInfo.roomQuantity) return;
      }
    }
    setRentTimeNum(rentTime);
  };

  // 操作按钮渲染
  const renderFooter = () => {
    if (dataInfo.type === 'add') {
      return (
        <div>
          <Button onClick={closeModal} className={styles.cancel}>
            {renderIntlLabel('order_action_cancel')}
          </Button>
          <Button type="primary" onClick={handleSubmitClick} className={styles.save}>
            {renderIntlLabel('channel_crs_action_confirm_to_submit_create_invitation_order')}
          </Button>
        </div>
      );
    }
    return (
      <div>
        <Button onClick={closeDetailModal} className={styles.cancel}>
          {renderIntlLabel('order_action_close')}
        </Button>
      </div>
    );
  };

  const inviteCalculationFun = (data) => {
    if (dataInfo.productId && dataInfo.type === 'add') {
      const obj = {
        commonCode,
        checkInDate: getDateTime(dataInfo.checkInTime),
        productId: dataInfo.productId,
        roomNum: rentTimeNum,
        roomTypeId: dataInfo.roomTypeId,
        type: dataInfo.occType,
        guestPhone: dataInfo.guestPhone,
      };
      if (dataInfo.occType === 'HOUR') {
        if (selectValue) {
          const newArr = selectValue.split('-');
          const [startTime, endTime] = newArr;
          getInviteCalculation({
            ...obj,
            ...data,
            expectedArrivalDate: startTime,
            expectedDepartureDate: endTime,
          });
        }
      } else {
        getInviteCalculation({
          ...obj,
          ...data,
          couponNos: selectHotelCouponList,
          checkOutDate: getDateTime(dataInfo.checkOutTime),
        });
      }
    }
  };

  // 积分是否抵扣
  const handlePointsChange = (values) => {
    setSelectedPoints(values);

    inviteCalculationFun({
      fixedPrice: null,
      fixedPriceDiscountAmount: null,
      usePoints: values?.[0],
    });
  };

  useEffect(() => {
    setFixedPrice(null);
    setAllPrice(null);
    setAllPriceDiscountAmount(null);
    setFixedPriceDiscountAmount(null);
    inviteCalculationFun({
      fixedPrice: null,
      fixedPriceDiscountAmount: null,
    });
  }, [rentTimeNum, dataInfo, selectHotelCouponList, selectValue]);

  // 提交
  const handleConfirmSubmit = async () => {
    setBtnLoading(true);
    try {
      const newParams: any = {
        checkInDate: getDateTime(dataInfo.checkInTime),

        commonCode,
        fixedPrice,
        fixedPriceDiscountAmount,
        guestName: dataInfo.guestName,
        guestPhone: dataInfo.guestPhone,
        operator: `${dataInfo.userInfo?.employeeNo}${dataInfo.userInfo?.name}`,
        productId: dataInfo.productId,
        roomNum: rentTimeNum,
        couponNos: selectHotelCouponList,
        roomTypeId: dataInfo.roomTypeId,
        type: dataInfo.occType,
        arrivalTime: '14:00',
        usePoints: selectedPoints?.[0],
      };

      if (dataInfo.occType === 'HOUR') {
        const newArr = selectValue.split('-');
        const [startTime, endTime] = newArr;
        newParams.expectedArrivalDate = startTime;
        newParams.expectedDepartureDate = endTime;
        newParams.stayLength = dataInfo.hours;
      }
      if (dataInfo.occType === 'DAILY') {
        newParams.departureDate = getDateTime(dataInfo.checkOutTime);
      }
      const { code, data } = await queryInviteSubmitOrder(newParams);
      if (code === 0) {
        setModalVisible(false);
        setConfirmVisible(false);
        setBtnLoading(false);
        setSelectOrderNo(data.orderId);
        setInviteOpen(true);
        onConfirm();
      }
    } catch (error) {
      setBtnLoading(false);
    }
  };

  const confirmFooter = () => {
    if (confirmType === '确认') {
      return (
        <div>
          <Button onClick={closeConfirmModal} className={styles.cancel}>
            {renderIntlLabel('order_action_cancel')}
          </Button>
          <Button
            type="primary"
            onClick={handleConfirmSubmit}
            className={styles.save}
            loading={btnLoading}
          >
            {renderIntlLabel('order_action_confirm_create')}
          </Button>
        </div>
      );
    }
    return (
      <div>
        <Button onClick={closeAllConfirmModal} className={styles.cancel}>
          {renderIntlLabel('order_action_unsave_close')}
        </Button>
        <Button type="primary" onClick={saveConfirmModal} className={styles.save}>
          {renderIntlLabel('order_action_continue_edit')}
        </Button>
      </div>
    );
  };

  const disabledHours = (startHours, endHours) => {
    const arr = [];
    for (let i = 0; i <= 24; i++) {
      if (i > endHours || i < startHours) {
        arr.push(i < 10 ? Number(`0${i}`) : i);
      }
    }
    return arr;
    // return [];
  };

  const disabledTime = (_current) => {
    return {
      disabledHours: () => disabledHours(16, 18),
    };
  };

  const handleTicket = () => {
    couponSelectorRef.current.openModal({
      hotelCouponList,
      couponNum: dataInfo.rentTimeNum * rentTimeNum,
      memberInfo: {
        custNo: dataInfo.custNo,
        guestName: dataInfo.guestName,
        guestPhone: dataInfo.guestPhone,
      },
    });
  };

  const handleTimeChange = (e) => {
    setSelectValue(e);
  };

  const handleOperate = (type) => {
    setOptionType(type);
    setEditting(false);
    if (type === 'save') {
      setAllPrice(allPriceDiscountAmount);
    }
    if (type === 'reset') {
      setFixedPriceDiscountAmount(null);
      setAllPrice(null);
      inviteCalculationFun({
        fixedPrice: null,
        fixedPriceDiscountAmount: null,
      });
    }
  };

  const handleConfirm = (data, list) => {
    // console.log(data, 'data');
    setIsFirst(false);
    setHotelCouponList(list);
    setSelectHotelCouponList(data);
  };

  return (
    <>
      <CouponSelector ref={couponSelectorRef} onConfirm={handleConfirm} />
      <Modal
        title={renderIntlLabel('channel_crs_title_confirm_order')}
        centered
        className={styles.confirmModalBody}
        visible={modalVisible}
        onCancel={closeModal}
        width={560}
        footer={renderFooter()}
      >
        <div className={styles.container}>
          <Spin spinning={calculationLoading}>
            <div className="space-between">
              <div className="groupLabel">{renderIntlLabel('channel_crs_title_reserve_info')}</div>
            </div>
            <div className={styles.reservationInfo}>
              {dataInfo.type === 'detail' ? (
                <FormItem label={renderIntlLabel('order_selfService_order_no')}>
                  {dataInfo?.orderNo}
                </FormItem>
              ) : null}
              {dataInfo?.occType === 'DAILY' ? (
                <FormItem label={renderIntlLabel('order_roomorder_arrival_time')}>
                  {dataInfo?.checkInTime}
                </FormItem>
              ) : (
                <FormItem label={renderIntlLabel('order_roomorder_arrival_time')}>
                  {getTimeByUtcOffset(dataInfo?.checkInTime, DATE_FORMAT)}
                </FormItem>
              )}

              {dataInfo?.occType === 'DAILY' ? (
                <FormItem label={renderIntlLabel('order_roomorder_predeparture_time')}>
                  {dataInfo?.checkOutTime}
                </FormItem>
              ) : null}
              {dataInfo?.occType === 'DAILY' ? (
                <FormItem label={renderIntlLabel('order_roomorder_checkin_duration_time')}>
                  {dataInfo?.rentTimeNum}
                  {renderIntlLabel('app_enum_occ_type_day_unit')}
                </FormItem>
              ) : null}
              {dataInfo?.occType === 'HOUR' ? (
                <FormItem label={renderIntlLabel('order_roomorder_checkin_period')}>
                  {/* <TimePicker.RangePicker
                  minuteStep={30}
                  format="HH:mm"
                  disabledTime={disabledTime}
                /> */}

                  <Select
                    style={{ width: 120 }}
                    onChange={handleTimeChange}
                    value={selectValue}
                    disabled={dataInfo.type === 'detail'}
                  >
                    {timeList.map((item) => {
                      return (
                        <Option
                          value={`${item.startTime}-${item.endTime}`}
                          key={item.startTime}
                          disabled={item.status === 0}
                        >
                          {`${item.startTime}-${item.endTime}`}
                        </Option>
                      );
                    })}
                  </Select>
                </FormItem>
              ) : null}
              <FormItem label={renderIntlLabel('channel_crs_label_crs_productionName')}>
                {dataInfo?.productName}
              </FormItem>
              <FormItem label={renderIntlLabel('order_roomorder_subscriber')}>
                {dataInfo?.guestName}
              </FormItem>
              <FormItem label={renderIntlLabel('order_roomorder_mobile')}>
                {dataInfo?.guestPhone}
              </FormItem>
              <FormItem
                label={renderIntlLabel('order_roomorder_room_counts')}
                className={styles.rentTimeItem}
              >
                <NumberWithHelper
                  value={rentTimeNum}
                  onChange={handleDayDurationChange}
                  disabled={dataInfo.type === 'detail'}
                />
                <span className={styles.rentTimeUnit}>{renderIntlLabel('app_room_unit')}</span>
              </FormItem>
            </div>
            <div className="space-between">
              <div className="groupLabel">{renderIntlLabel('channel_crs_label_sale')}</div>
            </div>
            <div className={styles.preferential}>
              {calculation.memberDiscount > 0 ? (
                <div className={styles.infoItem}>
                  {/* <div className={styles.lable}>网鱼会员 :</div> */}
                  <FormItem label={renderIntlLabel('channel_crs_label_member_of_wangyu')} />
                  <div className={styles.value}>
                    {calculation.vipDiscount}，{renderIntlLabel('channel_crs_label_reduce_totally')}
                    <span className={styles.amount}>
                      {parseFen2Currency(calculation.memberDiscount)}
                    </span>
                  </div>
                </div>
              ) : null}
              {calculation.crsPromotionDiscount > 0 ? (
                <div className={styles.infoItem}>
                  <FormItem label={calculation.crsPromotionName} />
                  <div className={styles.value}>
                    {renderIntlLabel('channel_crs_label_reduce_totally')}
                    {parseFen2Currency(calculation.crsPromotionDiscount)}
                  </div>
                </div>
              ) : null}
              {dataInfo.type === 'detail' || hotelCouponList.length > 0 ? (
                <>
                  <div className={styles.infoItem}>
                    <FormItem label={renderIntlLabel('channel_crs_label_coupon')} />
                    {calculation.couponDiscount > 0 ? (
                      <div className={styles.value}>
                        {renderIntlLabel('channel_crs_label_reduce_totally')}
                        {parseFen2Currency(calculation.couponDiscount)}
                      </div>
                    ) : null}
                  </div>
                  {dataInfo.type === 'add' ? (
                    <div className={styles.ticket}>
                      <div />
                      <div className={styles.value}>
                        <span className={styles.ticketNum}>
                          {renderIntlLabel('channel_crs_label_use_some', {
                            couponNumber: calculation.couponNumber || 0,
                          })}
                        </span>
                        <span className={styles.ticketBtn} onClick={() => handleTicket()}>
                          {renderIntlLabel('channel_crs_label_select_coupon')}
                        </span>
                      </div>
                    </div>
                  ) : null}
                </>
              ) : null}
              {fixedPriceDiscountAmount > 0 || calculation.fixedPriceDiscountAmount > 0 ? (
                <div className={styles.infoItem}>
                  <FormItem label={renderIntlLabel('channel_crs_label_store_yikou_price')} />
                  <div className={styles.value}>
                    {renderIntlLabel('channel_crs_label_reduce_totally')}
                    <span className={styles.amount}>
                      {parseFen2Currency(
                        fixedPriceDiscountAmount || calculation.fixedPriceDiscountAmount,
                      )}
                    </span>
                  </div>
                </div>
              ) : null}
              {/* 积分奖励 */}
              {!!calculation?.checkOutGrantPoints && (
                <div className={styles.infoItem}>
                  <FormItem label={renderIntlLabel('channel_hotel_reward_points')} />
                  <div className={styles.value}>
                    {renderIntlLabel('channel_hotel_leave_shop_give')}
                    <span className={styles.amount}>
                      {calculation?.checkOutGrantPoints}
                      {renderIntlLabel('channel_hotel_points')}
                    </span>
                  </div>
                </div>
              )}
            </div>

            {pointsSwitch &&
              !!calculation?.remainingPoints && (
                <>
                  <div className="space-between">
                    <div className="groupLabel">
                      {renderIntlLabel('channel_hotel_Points_against_room_charges')}
                    </div>
                  </div>
                  <div className={styles.housingExpenses}>
                    <div className={styles.checkbox}>
                      <Checkbox.Group value={selectedPoints} onChange={handlePointsChange}>
                        <Checkbox value={calculation?.availablePoints}>
                          <div>
                            {renderIntlLabel('channel_hotel_this_order_can_use')}
                            {calculation?.availablePoints}
                            {renderIntlLabel('channel_hotel_points_deduction')}
                            {parseFen2Currency(calculation?.pointsDeductionAmount)}
                          </div>
                          <div className={styles.totalPoints}>
                            {renderIntlLabel('channel_hotel_guest_account_hotel_points')}
                            <span>{calculation?.remainingPoints}</span>
                          </div>
                        </Checkbox>
                      </Checkbox.Group>
                    </div>
                  </div>
                </>
              )}

            <div className={styles.totalAmountCon}>
              <div className={styles.totalAmount}>
                {renderIntlLabel('channel_crs_label_actual_price')}：
                <span className={styles.amount}>
                  {/* {parseFen2Currency(allPrice ? allPrice * 100 : calculation.payAmount)} */}
                  {/* {`${currencySymbol}${parseFen2Yuan2Decimal(
                    allPrice ? (allPrice * 100).toFixed(0) : calculation.payAmount,
                  )}`} */}
                  {`${parseFen2Currency(
                    allPrice ? (allPrice * 100).toFixed(0) : calculation.payAmount,
                  )}`}
                </span>
                {dataInfo.type === 'add' ? (
                  <Popconfirm
                    title={
                      <div>
                        <div className={styles.editAmount}>
                          <Form.Item
                            label={renderIntlLabel('order_crs_label_actually_room_expense')}
                          >
                            <InputNumber
                              // allowClear
                              className={styles.inputNumber}
                              max={calculation.payAmount / 100}
                              prefix={intlCtx.TenantBaseConfig.CurrencySymbol}
                              placeholder={renderIntlLabel('order_frontDesk_validate_account')}
                              onChange={(e) => {
                                setAllPriceDiscountAmount(e);
                              }}
                              value={allPriceDiscountAmount}
                              onBlur={(e) => {
                                if (e.target.value) {
                                  // setAllPriceDiscountAmount(e.target.value);
                                  setFixedPrice((Number(e.target.value) * 100).toFixed(0));
                                  const countNum: any = (Number(e.target.value) * 100).toFixed(0);
                                  const newFixedPriceDiscountAmount = (
                                    calculation.payAmount - countNum
                                  ).toFixed(0);
                                  setFixedPriceDiscountAmount(newFixedPriceDiscountAmount);
                                }
                              }}
                            />
                          </Form.Item>
                          <Button
                            type="link"
                            onClick={() => handleOperate('save')}
                            style={{ marginLeft: 12 }}
                          >
                            {renderIntlLabel('order_action_save')}
                          </Button>
                          <Divider type="vertical" />
                          <Button
                            type="link"
                            onClick={() => {
                              setEditting(false);
                            }}
                          >
                            {renderIntlLabel('order_action_cancel')}
                          </Button>
                        </div>
                        <p className={styles.tips}>
                          {renderIntlLabel('channel_crs_msg_auto_calc_price', {
                            payAmount: parseFen2Currency(calculation.payAmount),
                          })}
                        </p>
                      </div>
                    }
                    icon={null}
                    placement="bottomRight"
                    overlayClassName="editPopConfirm"
                    visible={editting}
                  >
                    <Button
                      style={{ backgroundColor: 'inherit', border: 'none', color: '#3F79FF' }}
                      icon={<EditOutlined />}
                      onClick={() => {
                        setEditting(true);
                      }}
                    >
                      {renderIntlLabel('channel_crs_action_modify_actual_price')}
                    </Button>
                  </Popconfirm>
                ) : null}
              </div>
              {dataInfo.type === 'add' && fixedPriceDiscountAmount > 0 ? (
                <Button className={styles.restoreBtn} onClick={() => handleOperate('reset')}>
                  {renderIntlLabel('channel_crs_action_Restore_CRS_automatic_pricing')}
                </Button>
              ) : null}
            </div>
          </Spin>
        </div>
      </Modal>
      <Modal
        title={confirmTitle}
        centered
        visible={confirmVisible}
        onCancel={closeConfirmModal}
        width={560}
        footer={confirmFooter()}
      >
        {confirmType === '确认' ? (
          <div className={styles.confirmText}>
            {renderIntlLabel('channel_crs_msg_create_code_invitation_order_to_pay')}
          </div>
        ) : (
          <div className={styles.confirmText}>
            {renderIntlLabel('channel_crs_msg_content_will_be_lost')}
          </div>
        )}
      </Modal>
      <InviteModal
        onClose={() => {
          setInviteOpen(false);
        }}
        open={inviteOpen}
        orderNo={selectOrderNo}
      />
    </>
  );
});
